import { useTranslation } from 'next-i18next';

import { Badge, BadgeProps, Flex } from '@chakra-ui/react';

import { IconProps, IconsLinear, IconsTwoTone } from '@nocowanie/icons';

import { accommodationStaticSlice, useAppSelector } from '@app/state';

import { AccommodationBadgeType } from './accommodation-badge-type';
import { AccommodationBadgesProps } from './accommodation-badges.props';

export const AccommodationBadges = ({ badges, iconProps, ...props }: AccommodationBadgesProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'basicInformation',
    });

    const isBestseller = useAppSelector(accommodationStaticSlice.selectors.selectIsBestseller);
    const isRealTime = badges.includes(AccommodationBadgeType.InstantReservation);
    const iconPropsCommon: IconProps = {
        fontSize: '1.5em',
        mt: '-1px',
        mb: 0,
        ...iconProps,
    };
    const badgePropsCommon: BadgeProps = {
        size: { base: 'sm', md: 'lg' },
    };

    return (
        <Flex
            flexDirection={'row'}
            gap={2}
            flexWrap={{
                base: 'wrap',
                md: 'nowrap',
            }}
            alignItems={'center'}
            {...props}
        >
            {isRealTime ? (
                <Badge variant={'solid'} colorPalette={'green'} {...badgePropsCommon}>
                    <IconsLinear.Flash
                        color={'yellow.solid'}
                        fill={'yellow.solid'}
                        {...iconPropsCommon}
                    />
                    {t('badges.instantReservation')}
                </Badge>
            ) : null}
            {badges.includes(AccommodationBadgeType.ConfirmationUpTo24Hours) ? (
                <Badge variant={'subtle'} colorPalette={'green'} {...badgePropsCommon}>
                    <IconsTwoTone.Clock2 fill={'orange.solid'} {...iconPropsCommon} />
                    {t('badges.confirmationUpTo24Hours')}
                </Badge>
            ) : null}
            {badges.includes(AccommodationBadgeType.PromotedOnTV) ? (
                <Badge
                    variant={'solid'}
                    colorPalette="yellow"
                    fontWeight={'normal'}
                    {...badgePropsCommon}
                >
                    <IconsLinear.PlayCircle {...iconPropsCommon} />
                    {t('badges.promotedOnTV')}
                </Badge>
            ) : null}
            {isBestseller ? (
                <Badge
                    data-testid="bestseller"
                    variant={'outline'}
                    colorPalette={'gray'}
                    {...badgePropsCommon}
                >
                    <IconsLinear.Like1 accentFillColor={'orange.solid'} {...iconPropsCommon} />
                    {t('badges.bestseller')}
                </Badge>
            ) : null}
            {badges.includes(AccommodationBadgeType.NewAccommodation) ? (
                <Badge
                    data-testid="no-rating"
                    variant={'subtle'}
                    colorPalette="secondary"
                    {...badgePropsCommon}
                    ml={{
                        md: 'auto',
                    }}
                >
                    {t('badges.newAccommodation')}
                </Badge>
            ) : null}
        </Flex>
    );
};
